import { isNumber } from "@bonusmap/shared_ui/library";
import { BaseWebComponent } from "../base-components/base-web-component.js";
import { createChoicesInstance } from "../../utils/createChoicesInstance.js";

const { Choices } = window;

export class ValueArrayInput extends BaseWebComponent {
    static webComponentName: string = "value-array-input";
    static observedAttributes: string[] = ['type', 'name'];

    protected _templateId: string = "value-array-input";
    private _values: (string | number)[] = [];
    get values(): (string | number)[] {
        return this._values;
    }

    set values(newValues: (string | number)[]) {
        this._values = Array.isArray(newValues) ? [...newValues] : [];
        this.updateHiddenInputs();
        this.dispatchChangeEvent();
        console.log('Values updated:', this._values);
    }
    private inputElement!: HTMLInputElement;
    private choicesInstance!: InstanceType<Window['Choices']>;

    constructor() {
        super();
        this.initializeValue();
    }

    private initializeValue(): void {
        const initialValueString = this.textContent?.trim();
        if (initialValueString) {
            try {
                const parsedValue = JSON.parse(initialValueString);
                if (Array.isArray(parsedValue)) {
                    this._values = [...parsedValue];
                } else {
                    this._values = [];
                }
            } catch (e) {
                this._values = [];
                console.log("ValueArrayInput: Error parsing initial value:", e,{initialValueString});
            }
        }
        this.textContent = "";
    }

    get type(): 'number' | 'date' {
        const type = this.getAttribute('type');
        return type === 'date' ? 'date' : 'number';
    }

    get name(): string {
        return this.getAttribute('name') || '';
    }

    

    private initializeChoices(): void {
        this.choicesInstance = createChoicesInstance(this.inputElement, {
            removeItemButton: true,
            addItems: true,
            paste: true,
            editItems: true,
            duplicateItemsAllowed: false,
            searchEnabled: false,
            shouldSort: false,
            placeholder: true,
            placeholderValue: this.type === 'date'
                ? 'Aggiungi date (dd/mm/yyyy o yyyy-mm-dd)'
                : 'Aggiungi numeri',
            addItemText: (value: string) =>
                `Premi Invio per aggiungere \u003cb\u003e"${value}"\u003c/b\u003e`,
            noResultsText: '—',
            noChoicesText: '—',
            addItemFilter: (value: string) => this.isValidValue(value),
            // Cast a any per allineare ai tipi di Choices senza errori TS
            callbackOnCreateTemplates: (template) => {
                const _this = this;
                return {
                    item: (config: any, choice: any, removeItemButton: any) => {
                        if (_this.type === 'date') {
                            const value = choice.value;
                            const iso = /^\d{4}-\d{2}-\d{2}$/;
                            if (iso.test(value)) {
                                const [yyyy, mm, dd] = value.split('-');
                                choice.label = `${dd}/${mm}/${yyyy}`;
                            }
                        }
                        const originalItem = Choices.defaults.templates.item.call(
                            this.choicesInstance, config, choice, removeItemButton
                        );
                        return originalItem;
                    }
                };
            }
        });

        // Valori iniziali (come tag)
        if (this._values.length > 0) {
            const values = this._values.map(v => v.toString());
            this.choicesInstance.setValue(values);
        }

        // Eventi di aggiunta/rimozione tag
        this.inputElement.addEventListener('addItem', (event: any) => {
            const raw = event.detail.value as string;
            if (!this.isValidValue(raw)) {
                setTimeout(() => {
                    this.choicesInstance.removeActiveItemsByValue(raw);
                }, 0);
                return;
            }
            const processed = this.processValue(raw);
            if (processed !== null && !this._values.includes(processed)) {
                this._values.push(processed);
                this.updateHiddenInputs();
                this.dispatchChangeEvent();
            }
        });

        this.inputElement.addEventListener('removeItem', (event: any) => {
            const raw = event.detail.value as string;
            this._values = this._values.filter(v => v.toString() !== raw);
            console.log('Value removed:', raw, 'Remaining values:', this._values);
            this.updateHiddenInputs();
            this.dispatchChangeEvent();
        });
    }

    private isValidValue(value: string): boolean {
        if (!value || value.trim() === '') return false;
        const trimmedValue = value.trim();

        if (this.type === 'number') {
            // accetta anche "," come separatore decimale
            const numValue = parseFloat(trimmedValue.replace(',', '.'));
            return isNumber(numValue);
        } else if (this.type === 'date') {
            // accetta sia yyyy-mm-dd che dd/mm/yyyy
            const iso = /^\d{4}-\d{2}-\d{2}$/;
            const ita = /^\d{2}\/\d{2}\/\d{4}$/;

            if (iso.test(trimmedValue)) {
                const d = new Date(trimmedValue);
                return isNumber(d.getTime());
            }
            if (ita.test(trimmedValue)) {
                const [dd, mm, yyyy] = trimmedValue.split('/');
                const candidate = `${yyyy}-${mm}-${dd}`;
                const d = new Date(candidate);
                return isNumber(d.getTime());
            }
            return false;
        }

        return false;
    }


    private processValue(value: string): string | number | null {
        if (!value || value.trim() === '') return null;
        const trimmedValue = value.trim();

        if (this.type === 'number') {
            const numValue = parseFloat(trimmedValue.replace(',', '.'));
            return !isNumber(numValue) ? null : numValue;
        } else if (this.type === 'date') {
            // normalizza sempre in ISO yyyy-mm-dd
            const iso = /^\d{4}-\d{2}-\d{2}$/;
            const ita = /^\d{2}\/\d{2}\/\d{4}$/;
            if (iso.test(trimmedValue)) return trimmedValue;
            if (ita.test(trimmedValue)) {
                const [dd, mm, yyyy] = trimmedValue.split('/');
                return `${yyyy}-${mm}-${dd}`;
            }
            return null;
        }

        return null;
    }

    private updateChoices(): void {
        if (!this.choicesInstance) return;
        const values = this._values.map(v => v.toString());
        // setValue aggiunge i tag senza cancellare l'input
        if (values.length > 0) {
            this.choicesInstance.setValue(values);
        }
    }

    private formatDisplayValue(value: string | number): string {
        if (this.type === 'date' && typeof value === 'string') {
            try {
                return new Date(value).toLocaleDateString('it-IT');
            } catch (e) {
                return value.toString();
            }
        }
        return value.toString();
    }

    private updateHiddenInputs(): void {
        // Rimuovi tutti gli input hidden esistenti
        const existingInputs = this.querySelectorAll('input[type="hidden"]');
        existingInputs.forEach(input => input.remove());

        // Crea nuovi input hidden per ogni valore
        this._values.forEach(value => {
            const hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.name = `${this.name}[]`;
            hiddenInput.value = value.toString();
            this.appendChild(hiddenInput);
        });
    }

    private dispatchChangeEvent(): void {
        this.dispatch('value-changed', {
            name: this.name,
            values: this._values,
            type: this.type
        });
    }

    protected buildRenderingData(): Record<string, any> {
        return {
            ...super.buildRenderingData(),
            type: this.type,
            name: this.name,
            values: this._values
        };
    }

    protected afterRender(): void {
        super.afterRender();
        this.inputElement = this.querySelector('input[type="text"]') as HTMLInputElement;
        if (this.inputElement) {
            this.initializeChoices();
            // Restringi l'input ai soli numeri/decimali quando type=number
            if (this.type === 'number') {
                // intercetta keydown dall'input interno di Choices (bubbling)
                this.addEventListener('keydown', (ev: KeyboardEvent) => {
                    const tgt = ev.target as HTMLElement;
                    if (!(tgt instanceof HTMLInputElement) || !tgt.classList.contains('choices__input')) return;

                    const allowedControl = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter'];
                    if (allowedControl.includes(ev.key)) return;

                    const isDigit = /\d/.test(ev.key);
                    const isDecimalSep = ev.key === '.' || ev.key === ',';
                    if (!isDigit && !isDecimalSep) {
                        ev.preventDefault();
                    }
                }, true);

                // blocca paste con caratteri non numerici
                this.addEventListener('paste', (ev: ClipboardEvent) => {
                    const tgt = ev.target as HTMLElement;
                    if (!(tgt instanceof HTMLInputElement) || !tgt.classList.contains('choices__input')) return;

                    const text = (ev.clipboardData?.getData('text') || '').trim();
                    if (!/^\d+(?:[.,]\d+)?$/.test(text)) {
                        ev.preventDefault();
                    }
                }, true);
            }
        }
    }

    protected onDisconnected(): void {
        super.onDisconnected();
        if (this.choicesInstance) {
            this.choicesInstance.destroy();
        }
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
        super.attributeChangedCallback(name, oldValue, newValue);
    }
}